/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import { useSnackbar } from 'src/components/snackbar';
import { useBoolean } from 'src/hooks/use-boolean';
import { useAxiosClientContext } from 'src/http/context/axios-client-context';
import { BenefitsConfigRepository } from 'src/repositories/benefits-config/benefits-config.repository';
import { useFilterStore } from 'src/stores/filters/filters.store';
import { BenefitsConfig } from 'src/types/benefits-config/benefit-config';

export const useCategories = () => {
  const { order, orderBy, page, limit, total, filters, setOrder, setOrderBy, setLimit, setPage, setTotal, resetFilters } = useFilterStore();
  const [isLoading, setIsLoading] = useState(true);
  const [checked, setChecked] = useState<string[]>([]);
  const [data, setData] = useState<BenefitsConfig[]>([]);
  const enqueueSnackbar = useSnackbar();
  const [rowToDelete, setRowToDelete] = useState<BenefitsConfig>();
  const useConfirmDeleteDialog = useBoolean(false);

  const api = useAxiosClientContext();
  const repository = new BenefitsConfigRepository(api);

  const getData = async () => {
    try {
      setIsLoading(true);
      const response = await repository.getData({
        order,
        orderBy,
        page: page + 1,
        limit,
        ...filters,
      });
      setData(response.value.data.map((res: any) => ({ ...res, id: res.id })));
      setTotal(response.value.total);
      setChecked(response.value.data.filter((r: any) => r.status === 'active').map((r: any) => r.id));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNew = () => {
    setData([
      {
        id: `-`,
        name: '',
        type: '',
        count: 0,
        status: 'active',
      },
      ...data,
    ]);
    setChecked([...checked, `-`]);
  };

  const handleCheckRow = async (id: string) => {
    const response = await repository.changeStatus(id);
    if (response.isLeft()) {
      enqueueSnackbar.toastWarning('Erro ao alterar status!');
    }
    if (checked.includes(id)) {
      setChecked(checked.filter((c) => c !== id));
    } else {
      setChecked([...checked, id]);
    }
  };

  const createSortHandler = (id: string) => {
    if (orderBy === id) {
      if (order === 'asc') {
        setOrder('desc');
      } else {
        setOrder('asc');
      }
    } else if (order !== id) {
      setOrderBy(id);
      setOrder('asc');
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (row: any) => {
    useConfirmDeleteDialog.onTrue();
    setRowToDelete(row);
  };

  const handleConfirmDelete = async () => {
    useConfirmDeleteDialog.onFalse();
    try {
      if (rowToDelete?.id !== '') {
        const response = await repository.delete(rowToDelete!.id);
        if (response.isLeft()) {
          throw new Error(response.value.message);
        } else {
          enqueueSnackbar.toastWarning('Removido com sucesso!');
          getData();
        }
      } else {
        setData(data.filter((r) => r.id !== rowToDelete.id));
      }
    } catch (error) {
      enqueueSnackbar.toastWarning(error);
    }
  };

  useEffect(() => {
    getData();
    return () => {
      resetFilters();
    };
  }, []);

  useEffect(() => {
    getData();
  }, [order, orderBy, page, limit]);

  return {
    data,
    isLoading,
    getData,
    handleAddNew,
    checked,
    total,
    page,
    limit,
    order,
    orderBy,
    createSortHandler,
    handleChangePage,
    handleChangeRowsPerPage,
    handleCheckRow,
    handleDelete,
    handleConfirmDelete,
    useConfirmDeleteDialog,
  };
};
